import { QueryKey, useQuery } from '@tanstack/react-query';

import * as Api from 'contracts/api';
import { RequestAgent } from 'modules/request-agent';
import { useNotify } from 'pages/components/SnackBarProvider';
import { ExposedQueryFunctionContext, UseExposedQueryOptions } from 'queries/types';
import { logger } from 'services/logger';

import { BASE_URL, FALLBACK_POSTS } from './constants';

export function useBlogPostsFeed<TData = Api.Blog.Feed.Response>(
    options?: UseExposedQueryOptions<Api.Blog.Feed.Response, Error, TData>
) {
    const notify = useNotify();
    return useQuery<Api.Blog.Feed.Response, Error, TData, QueryKey>({
        ...options,
        queryKey: Api.Blog.Feed.QueryKey(),
        queryFn: (ctx) =>
            fetchBlogPostsFeed({
                ...ctx,
                agent: new RequestAgent({ baseUrl: BASE_URL }),
                onFailure: (error) => notify.openNotification({ level: 'error', message: error.message })
            })
    });
}

export async function fetchBlogPostsFeed(context: ExposedQueryFunctionContext<Api.Blog.Feed.Response>) {
    const agent = context.agent || new RequestAgent();

    try {
        const response = await agent.public
            .get<Api.Blog.Feed.Response>(Api.Blog.Feed.Endpoint())
            .catch((error) => {
                logger.error(error.message);
                return FALLBACK_POSTS;
            })
            .then((output) => {
                return output.map((item) => ({
                    ...item,
                    image: item.image ? new URL(item.image, BASE_URL).href : item.image,
                    link: item.link ? new URL(item.link, BASE_URL).href : item.link
                }));
            });
        await context?.onSuccess?.(response);
        return response;
    } catch (error) {
        await context?.onFailure?.(error);
        throw error;
    }
}
